import { useEffect } from "react";

import { observer } from "mobx-react-lite";

import { NodeBE } from "src/api/flowTypes";
import { ErrorBaseInfo } from "src/api/types";
import { ChatBody } from "src/base-components/Chat/ChatBody";
import { ChatInputArea } from "src/base-components/Chat/ChatInputArea";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import {
  NodeConversationContextContextTypeEnum,
  ToolUse,
} from "src/clients/copilot";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { ConversationMessageItem } from "src/copilot/ConversationMessageItem";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { DeletionModal } from "src/copilot/DeletionModal";
import { EmptyState } from "src/copilot/EmptyState";
import { NodeSuggestionType } from "src/copilot/NodeSuggestion";
import { PromptBox } from "src/copilot/PromptBox";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { TitleRightContent } from "src/copilot/TitleRightContent";
import { ToolApprovalResultCard } from "src/copilot/ToolApprovalResultCard";
import { createToolApprovalMessage } from "src/copilot/toolUtils";
import { useCopilotConversation } from "src/copilot/useCopilotConversation";
import { useFirstDataSample } from "src/copilot/useFirstDataSample";
import { getToolApprovalOrResult } from "src/copilot/utils";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import {
  NoCompletionDataAvailable,
  useNodeCompletionData,
} from "src/nodeEditor/api/queries";
import { useHandleApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useGraphStore } from "src/store/StoreProvider";
import { GraphTransform } from "src/utils/GraphUtils";

type WindowData = {
  nodeId: string;
  conversationId: string;
  resetConversationId: () => void;
  error?: ErrorBaseInfo;
};

const assertWindowData = (data: unknown): data is WindowData => {
  return Boolean(
    data &&
      typeof data === "object" &&
      "nodeId" in data &&
      "conversationId" in data &&
      "resetConversationId" in data,
  );
};

export const CopilotWindow: React.FC<FloatingWindowProps> = observer(
  ({ computePosition, id, data: windowData }) => {
    if (!assertWindowData(windowData)) {
      throw new Error("Invalid floating window data", windowData);
    }

    const { position, windowRef } = useFloatingWindowPosition(computePosition);
    const { nodes, selectedNode, setSelectedNode, nodesArray } =
      useGraphStore();
    const node = nodes.get(windowData.nodeId) ?? null;
    const {
      handleApply: onApply,
      isDeletionModalOpen,
      deletionInfo,
      handleConfirmDelete,
      handleCancelDelete,
    } = useHandleApplyNodeSuggestion();

    const { version } = useAuthoringContext();
    const canEditFlowVersion = useCanAuthoringEditFlowVersion();
    const dataSample = useFirstDataSample({ node });
    const { data: nodeTypingData } = useNodeCompletionData(
      version.id,
      node?.id,
      node?.type === NODE_TYPE.LOOP_NODE,
    );

    const {
      variables,
      messages,
      data,
      isLoading,
      isError,
      failureCount,
      handleClose,
      onSendMessage,
      handleReset,
      indexToAnimate,
      listRef,
      prompt,
      setPrompt,
    } = useCopilotConversation({
      conversationId: windowData.conversationId,
      windowId: id,
      nodeId: windowData.nodeId,
      versionId: version.id,
      resetConversationId: windowData.resetConversationId,
    });

    const handleSendMessage = async (
      prompt: string | null,
      toolApproval?: ToolUse,
      mentionedNodes: NodeBE[] = [],
    ) => {
      if (!node) return;

      const versionParameters = version.parameters?.reduce(
        (acc, p) => ({
          ...acc,
          [p.name]: p.value,
        }),
        {},
      );

      onSendMessage(
        prompt,
        {
          node_definition: GraphTransform.nodeReverseMapper(node)!,
          input_schema: version.input_schema,
          output_schema: version.output_schema,
          data_sample: dataSample,
          parameters: versionParameters,
          data_typing:
            nodeTypingData === NoCompletionDataAvailable
              ? undefined
              : nodeTypingData,
          context_type: NodeConversationContextContextTypeEnum.NODE,
          mentioned_nodes:
            mentionedNodes.length > 0 ? mentionedNodes : undefined,
        },
        toolApproval,
      );
    };

    const handlePromptClick = (
      prompt: string | null,
      mentionedNodes: NodeBE[] = [],
    ) => {
      handleSendMessage(prompt, undefined, mentionedNodes);
    };

    const handleApproveTool = (toolUse: ToolUse, approved: boolean) => {
      const approvalMessage = createToolApprovalMessage(toolUse, approved);

      handleSendMessage(
        approvalMessage.content,
        approvalMessage.tool_use ?? undefined,
      );
    };

    const handleApply = (suggestion: NodeSuggestionType) => {
      if (!data) return;

      if (!selectedNode || selectedNode.id !== windowData.nodeId) {
        setSelectedNode(windowData.nodeId);
      }

      // needs to be done in the next cycle to ensure the sidepane is open
      setTimeout(() => {
        onApply({
          nodeId: windowData.nodeId,
          suggestion,
          completionId: data.completion_id,
          conversationId: windowData.conversationId,
        });
      });
    };

    useEffect(() => {
      if (windowData.nodeId && windowData.error)
        setPrompt((oldPrompt) => {
          if (oldPrompt.length < 1 && windowData.error) {
            return `I'm receiving \`${windowData.error.msg}\`, can you suggest a fix?`;
          }
          return oldPrompt;
        });
    }, [setPrompt, windowData.error, windowData.nodeId]);

    if (!node) {
      return null;
    }

    const loadingMode = (() => {
      if (isLoading && variables?.message === null) return "regenerating";
      if (isLoading && failureCount > 0) return "retrying";
      return "loading";
    })();

    return (
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        height="h-150"
        helpText={
          <TitleHelpContent
            description="Copilot helps you create, edit, and debug nodes more efficiently. It can
        explain existing logic, suggest improvements, and assist with common
        coding tasks. Copilot is currently supported for:"
            supportedOptions={["Code nodes", "Rule nodes", "Assignment nodes"]}
          />
        }
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={<CopilotTitle />}
        titleRight={<TitleRightContent node={node} onReset={handleReset} />}
        draggable
        lockNodeEditor
        onClose={handleClose}
      >
        <ChatBody>
          {data ? (
            <ChatMessageList ref={listRef}>
              {data.messages.map((message, index) => {
                const toolApprovalOrResult = getToolApprovalOrResult(message);
                if (toolApprovalOrResult) {
                  return (
                    <li key={`${message.role}-${index}`}>
                      <ToolApprovalResultCard toolUse={toolApprovalOrResult} />
                    </li>
                  );
                }

                const isLastMessage = index === data.messages.length - 1;

                return (
                  <ConversationMessageItem
                    key={`${message.role}-${index}`}
                    animate={indexToAnimate === index}
                    canEditFlowVersion={canEditFlowVersion}
                    completionId={isLastMessage ? data.completion_id : null}
                    conversationId={data.conversation_id}
                    isLastMessage={isLastMessage}
                    isLoading={isLoading}
                    message={message}
                    resourceData={{ node, nodesArray }}
                    resourceType={NodeConversationContextContextTypeEnum.NODE}
                    onApply={(suggestion) =>
                      handleApply(suggestion as NodeSuggestionType)
                    }
                    onRegenerate={() => handlePromptClick(null)}
                    onToolApproval={handleApproveTool}
                  />
                );
              })}
              {isLoading && <ChatLoadingIndicator mode={loadingMode} />}
            </ChatMessageList>
          ) : (
            <EmptyState
              nodeType={node.type}
              onPromptClick={handlePromptClick}
            />
          )}
          <ChatInputArea>
            <PromptBox
              disabled={isLoading}
              errored={isError}
              mentionTextPlaceholder="Use @ to refer to other nodes"
              minified={messages.length > 0}
              resource={nodesArray}
              resourceType={NodeConversationContextContextTypeEnum.NODE}
              value={prompt}
              onChange={(v) => setPrompt(v)}
              onReset={handleReset}
              onSubmit={(content, mentionedResources) => {
                const trimmedContent = content.trim();
                if (trimmedContent) {
                  handlePromptClick(
                    trimmedContent,
                    mentionedResources as NodeBE[],
                  );
                  setPrompt("");
                }
              }}
            />
          </ChatInputArea>
        </ChatBody>
        {isDeletionModalOpen && (
          <DeletionModal
            deletionInfo={deletionInfo}
            isOpen={isDeletionModalOpen}
            node={node}
            onCancelDelete={handleCancelDelete}
            onConfirmDelete={handleConfirmDelete}
          />
        )}
      </FloatingWindowInternal>
    );
  },
);
